import { useEffect, useCallback, useRef } from 'react';
import { logger } from '@/lib/logger';

/**
 * Preload functions for lazy-loaded routes
 * These match the lazy() imports in App.tsx
 */
const routePreloaders = {
  // Core pages
  dashboard: () => import('../pages/Dashboard'),
  index: () => import('../pages/Index'),
  auth: () => import('../pages/Auth'),
  
  // Secondary pages - CRITICAL for offline access
  settings: () => import('../pages/Settings'),
  history: () => import('../pages/History'),
  reports: () => import('../pages/Reports'),
  help: () => import('../pages/Help'),
  careTeam: () => import('../pages/CareTeam'),
  export: () => import('../pages/Export'),
  
  // Legal pages
  terms: () => import('../pages/TermsOfService'),
  privacy: () => import('../pages/PrivacyPolicy'),
  faq: () => import('../pages/FAQ'),
  medicalDisclaimer: () => import('../pages/MedicalDisclaimer'),
  aiDisclaimer: () => import('../pages/AIWellnessDisclaimer'),
  
  // Dashboard tab components - lazy loaded for code splitting
  homeTab: () => import('../components/dashboard/HomeTab'),
  trackTab: () => import('../components/dashboard/TrackTab'),
  medsTab: () => import('../components/dashboard/MedsTab'),
  appointmentsTab: () => import('../components/dashboard/AppointmentsTab'),
  moreTab: () => import('../components/dashboard/MoreTab'),
  betaTab: () => import('../components/dashboard/BetaTab'),
};

type RouteKey = keyof typeof routePreloaders;

// Track which routes have been preloaded to avoid duplicate imports
const preloadedRoutes = new Set<RouteKey>();

// Track if tab preloading has been scheduled this session
let tabPreloadScheduled = false;

// Track if first shell has rendered (set by perfMetrics)
let firstShellRendered = false;

/**
 * Signal that first shell has rendered - preloading can begin
 */
export const markFirstShellRendered = () => {
  firstShellRendered = true;
};

/**
 * DEV-only logging for chunk preload events
 */
const logChunkEvent = (event: string, details?: Record<string, unknown>) => {
  if (import.meta.env.DEV) {
    logger.log(`[ChunkPreload] ${event}`, details);
  }
};

/**
 * Preload a specific route's chunk with cache tracking
 */
export const preloadRoute = (route: RouteKey): Promise<void> | undefined => {
  if (preloadedRoutes.has(route)) {
    logChunkEvent('cache_hit', { route });
    return;
  }
  
  preloadedRoutes.add(route);
  logChunkEvent('preload_start', { route });
  
  return routePreloaders[route]?.()
    .then(() => {
      logChunkEvent('preload_done', { route });
    })
    .catch((err) => {
      logChunkEvent('preload_error', { route, error: err.message });
      // Remove from set so it can be retried
      preloadedRoutes.delete(route);
    });
};

/**
 * Preload multiple routes in parallel
 */
export const preloadRoutes = (routes: RouteKey[]): Promise<void[]> => {
  logChunkEvent('batch_start', { routes });
  return Promise.all(routes.map(r => preloadRoute(r) ?? Promise.resolve()));
};

/**
 * Schedule a callback during idle time with fallback
 * CRITICAL: Only runs after first shell render to avoid blocking initial paint
 */
const scheduleIdle = (callback: () => void, timeout: number = 5000) => {
  const execute = () => {
    if ('requestIdleCallback' in window) {
      (window as Window & { requestIdleCallback: (cb: IdleRequestCallback, opts?: { timeout: number }) => number })
        .requestIdleCallback(callback, { timeout });
    } else {
      setTimeout(callback, 100);
    }
  };

  // Wait for first shell render before scheduling any preloading
  if (firstShellRendered) {
    execute();
  } else {
    // Check again after a short delay
    setTimeout(() => {
      if (firstShellRendered) {
        execute();
      } else {
        // Fallback: execute anyway after 1s to avoid blocking forever
        setTimeout(execute, 1000);
      }
    }, 200);
  }
};

/**
 * Preload all dashboard tab chunks during idle time
 * Call this once after dashboard mounts for instant tab switching
 * 
 * CRITICAL: Now includes More tab routes (history, reports, export) on ALL devices
 * to prevent "Something went wrong" errors when navigating offline
 */
export const preloadAllTabChunks = () => {
  // Only schedule once per session
  if (tabPreloadScheduled) return;
  tabPreloadScheduled = true;

  logChunkEvent('chunk_preload_start');

  // Check network conditions
  const connection = (navigator as Navigator & { connection?: { saveData?: boolean; effectiveType?: string } }).connection;
  if (connection?.saveData || connection?.effectiveType === 'slow-2g' || connection?.effectiveType === '2g') {
    logChunkEvent('skipped_slow_connection', { effectiveType: connection?.effectiveType });
    return; // Skip on slow connections
  }

  // Priority 1: Most commonly used tabs (high priority, short timeout)
  scheduleIdle(() => {
    preloadRoutes(['homeTab', 'trackTab', 'medsTab']);
  }, 2000);

  // Priority 2: Secondary tabs (medium priority)
  scheduleIdle(() => {
    preloadRoutes(['appointmentsTab', 'moreTab', 'betaTab']);
  }, 4000);

  // Priority 3: More tab destinations - CRITICAL for offline navigation
  // These are now preloaded on ALL devices (including mobile) to prevent
  // "Something went wrong" errors when offline
  scheduleIdle(() => {
    logChunkEvent('preloading_more_tab_routes');
    preloadRoutes(['history', 'reports', 'export', 'settings']);
  }, 6000);

  // Priority 4: Help page (lowest priority)
  scheduleIdle(() => {
    preloadRoutes(['help']);
    logChunkEvent('chunk_preload_done');
  }, 8000);
};

/**
 * Hook that preloads all tab chunks after dashboard mounts
 * Uses requestIdleCallback for non-blocking preloading
 */
export const useTabChunkPreloader = () => {
  const hasScheduled = useRef(false);

  useEffect(() => {
    if (hasScheduled.current) return;
    hasScheduled.current = true;

    // Small delay to let initial render complete
    const timeoutId = setTimeout(() => {
      preloadAllTabChunks();
    }, 500);

    return () => clearTimeout(timeoutId);
  }, []);
};

/**
 * Hook that preloads common routes after initial render
 * Uses requestIdleCallback for non-blocking preloading
 */
export const useRoutePreloader = () => {
  useEffect(() => {
    // Don't preload on slow connections or data-saver mode
    const connection = (navigator as Navigator & { connection?: { saveData?: boolean; effectiveType?: string } }).connection;
    if (connection?.saveData || connection?.effectiveType === 'slow-2g' || connection?.effectiveType === '2g') {
      return;
    }

    // Longer delay on mobile for better initial performance
    const isMobile = window.innerWidth < 768;
    const initialDelay = isMobile ? 3000 : 1500;

    const timeoutId = setTimeout(() => {
      const schedulePreload = (callback: () => void, priority: 'high' | 'low' = 'low') => {
        const timeout = priority === 'high' ? 2000 : 5000;
        scheduleIdle(callback, timeout);
      };

      // Only preload core dashboard tabs on mobile
      if (isMobile) {
        schedulePreload(() => {
          preloadRoutes(['homeTab', 'trackTab', 'medsTab']);
        }, 'low');
      } else {
        // Desktop: preload more aggressively - homeTab first as it's the default
        schedulePreload(() => {
          preloadRoutes(['homeTab', 'trackTab', 'medsTab', 'appointmentsTab']);
        }, 'high');

        schedulePreload(() => {
          preloadRoutes(['moreTab', 'betaTab']);
        }, 'low');

        schedulePreload(() => {
          preloadRoutes(['settings', 'help', 'history']);
        }, 'low');
      }
    }, initialDelay);

    return () => clearTimeout(timeoutId);
  }, []);
};

/**
 * Hook that preloads a route on hover/focus
 * Attach to navigation links for instant loading
 */
export const useHoverPreload = (route: RouteKey) => {
  const handlePreload = useCallback(() => {
    preloadRoute(route);
  }, [route]);

  return {
    onMouseEnter: handlePreload,
    onFocus: handlePreload,
    onTouchStart: handlePreload,
  };
};

/**
 * Preload legal pages (called when user hovers over footer links)
 */
export const preloadLegalPages = () => {
  preloadRoutes(['terms', 'privacy', 'faq', 'medicalDisclaimer']);
};
